// React and related imports
import React from 'react';

// Flowbite UI components and icons imports
import { Sidebar as FbSidebar } from 'flowbite-react';
import {
  HiChartPie,
  HiOutlineCollection,
  HiOutlineDocumentAdd,
  HiUser,
  HiSpeakerphone,
  HiAcademicCap,
} from 'react-icons/hi';
import { BiSupport } from 'react-icons/bi';
import { HiScale } from 'react-icons/hi2';

// i18n translation hook
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

// Types and Interfaces
import {
  ISidebarItem,
  ISubItem,
} from '../../common/interfaces/SidebarItem.interface';

// React Router Link
import { NavLink, useLocation } from 'react-router-dom';

// User Info Context
import { useUserInfo } from '../../auth/UserInfoProvider';
import { canOpen } from '../../auth/roleMatrix';
import { DashboardUserRole } from '../../common/enums/DashboardUserRole';

/**
 * The sidebar for a role. Every link's visibility comes from the role matrix, and a group shows when any
 * of its links does, so the menu never offers a page the role cannot open (nor hides one it can).
 */
export const getSidebarItems = (
  t: TFunction,
  role: DashboardUserRole | undefined
): ISidebarItem[] => {
  const link = (label: string, path: string): ISubItem => ({
    label,
    link: path,
    isUserAllowed: canOpen(role, path),
  });
  const group = (
    icon: ISidebarItem['icon'],
    label: string,
    subItems: ISubItem[]
  ): ISidebarItem => ({
    icon,
    label,
    subItems,
    isUserAllowed: subItems.some((item) => item.isUserAllowed),
  });
  const single = (
    icon: ISidebarItem['icon'],
    label: string,
    path: string
  ): ISidebarItem => ({ icon, ...link(label, path) });

  return [
    single(HiChartPie, t('sidebar.dashboard'), '/'),
    group(HiOutlineDocumentAdd, t('sidebar.contentManagement'), [
      link(t('sidebar.contentContext'), '/content-context'),
      link(t('sidebar.content'), '/content'),
      link(t('sidebar.media'), '/media'),
      link(t('sidebar.questions'), '/questions'),
      link(t('sidebar.answers'), '/answers'),
      link(
        t('sidebar.notificationConfiguration'),
        '/notification-configuration'
      ),
      link(t('sidebar.questionnaire'), '/questionnaire'),
    ]),
    single(
      HiOutlineCollection,
      t('sidebar.versionsManagement'),
      '/versions-management'
    ),
    group(HiUser, t('sidebar.usersManagement'), [
      link(t('sidebar.users'), '/user-list'),
    ]),
    group(HiSpeakerphone, t('sidebar.broadcast'), [
      link(t('sidebar.broadcastList'), '/broadcast'),
      link(t('sidebar.broadcastCreate'), '/broadcast/create'),
    ]),
    group(HiScale, t('sidebar.istifta'), [
      link(t('sidebar.istifta'), '/istifta'),
      link(t('istiftaLinks.sidebar'), '/istifta/links'),
    ]),
    single(
      BiSupport,
      t('sidebar.technicalSupportManagement'),
      '/technical-support'
    ),
    single(HiAcademicCap, t('sidebar.sessions'), '/sessions'),
  ];
};

export const ItemsGroup: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { userInfo } = useUserInfo();
  const sidebarItems = getSidebarItems(t, userInfo?.role);

  return (
    <FbSidebar.ItemGroup>
      {sidebarItems.map((item) =>
        !item.isUserAllowed ? null : item.subItems &&
          item.subItems.length > 0 ? (
          <FbSidebar.Collapse
            key={item.label}
            className="sidebar__item collapse__item"
            icon={item.icon}
            label={item.label}
            open={item.subItems.some(
              (subItem) => subItem.link === location.pathname
            )}
          >
            <SubItems subItems={item.subItems} />
          </FbSidebar.Collapse>
        ) : (
          <FbSidebar.Item
            key={item.label}
            className={`sidebar__item`}
            icon={item.icon}
            as={NavLink}
            to={item.link || ''}
            end
          >
            {item.label}
          </FbSidebar.Item>
        )
      )}
    </FbSidebar.ItemGroup>
  );
};

const SubItems: React.FC<{ subItems: ISubItem[] }> = ({ subItems }) => {
  return subItems ? (
    <>
      {subItems.map((subItem) =>
        !subItem.isUserAllowed ? null : (
          <FbSidebar.Item
            key={subItem.label}
            className={`sidebar__item`}
            as={NavLink}
            to={subItem.link || ''}
            end
          >
            {subItem.label}
          </FbSidebar.Item>
        )
      )}
    </>
  ) : null;
};
